import "@babel/polyfill";
import envVariable from "../presentation/util/envVariable";
import encoder from "../presentation/util/encoder";
import axios from "axios";
import storage from "../presentation/util/storage";
import Caller from "../domain/Api/Caller";

interface OAuth2Token {
    access_token: string,
    refresh_token: string,
    expires_in: number,
    token_type: string,
    expires_at: number
}

export default class Authentication {
    static urlToken = envVariable('REACT_APP_OAUTH_ACCESS_TOKEN_URL');
    static callbackUrl = envVariable('REACT_APP_OAUTH_CALLBACK_URL');
    static clientId = envVariable('REACT_APP_OAUTH_CLIENT_ID');
    static localStorageTokenName = envVariable('REACT_APP_LOCAL_STORAGE_TOKEN_NAME');
    static codeVerifier = localStorage.getItem('code_verifier') ?? '';
    static urlAuthorisationServer = envVariable('REACT_APP_OAUTH_AUTH_URL');
    static urlSsoCheck = envVariable('REACT_APP_API_HOST')+'/sso-check';

    static getOAuth2Token(): OAuth2Token | null {
        const oauth = localStorage.getItem(Authentication.localStorageTokenName)
        if (!oauth) {
            return null
        }
        return JSON.parse(oauth)
    }

    static getToken(): string {
        const oauth = Authentication.getOAuth2Token()

        return `${oauth?.token_type} ${oauth?.access_token}`
    }

    static async authorize(sso: boolean, extranetToken: string|null = null): Promise<boolean> {
        try {
            // generate CodeVerifier
            const array = new Uint32Array(56 / 2);
            window.crypto.getRandomValues(array);
            const codeVerifier = Array.from(array, encoder.dec2hex).join('');
            localStorage.setItem('code_verifier', codeVerifier);

            const code_challenge = await encoder.generateCodeChallengeFromVerifier(
                codeVerifier
            );

            localStorage.setItem('code_challenge', code_challenge);

            let urlToRedirect = `${Authentication.urlAuthorisationServer}?response_type=code&state=&client_id=${Authentication.clientId}&scope=&redirect_uri=${Authentication.callbackUrl}&code_challenge=${code_challenge}&code_challenge_method=S256`
            if (sso) {
                urlToRedirect += '&sso=1'
            }
            if (extranetToken) {
                urlToRedirect += '&extranet='+extranetToken
            }
            window.location.href = urlToRedirect
            return true;
        } catch (e) {
            return false;
        }
    }

    static async login(code: string|null): Promise<boolean> {
        if (null === code) {
            return false
        }

        const options = new URLSearchParams();
        options.append('grant_type', 'authorization_code');
        options.append('client_id', this.clientId);
        options.append('redirect_uri', this.callbackUrl);
        options.append('code', code);
        options.append('code_verifier', this.codeVerifier);

        let success = false;
        await fetch(this.urlToken, {
            method: 'POST',
            body: options,
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded; charset=utf-8',
            }
        })
            .then(res => res.json())
            .then((response) => {
                if (response.access_token) {
                    response.expires_at = new Date().setSeconds(new Date().getSeconds() + response.expires_in);
                    localStorage.setItem(this.localStorageTokenName, JSON.stringify(response));
                    localStorage.removeItem('referential');
                    success = true;
                }
            })
        ;

        return success;
    }

    static async isLogged(): Promise<boolean> {
        const oauth = Authentication.getOAuth2Token()

        if (oauth?.access_token) {
            if ((new Date()).getTime() > oauth?.expires_at) {
                return await Authentication.refreshToken();
            }
            return true;
        }
        return false;
    }

    static async refreshToken(): Promise<boolean> {
        const oauth = Authentication.getOAuth2Token()

        let success = false;
        if (oauth?.refresh_token) {
            const codeVerifier = localStorage.getItem('code_verifier') ?? '';
            const options = new URLSearchParams();
            options.append('grant_type', 'refresh_token');
            options.append('client_id', this.clientId);
            options.append('redirect_uri', this.callbackUrl);
            options.append('client_secret', codeVerifier);
            options.append('refresh_token', oauth.refresh_token);

            await fetch(this.urlToken, {
                method: 'POST',
                body: options,
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded; charset=utf-8',
                }
            })
                .then(res => res.json())
                .then((response) => {
                    if (response.access_token) {
                        response.expires_at = new Date().setSeconds(new Date().getSeconds() + response.expires_in);
                        localStorage.setItem(this.localStorageTokenName, JSON.stringify(response));
                        success = true;
                    } else {
                        window.location.replace('/logout')
                    }
                }).catch(() => {
                    window.location.replace('/logout')
                })
            ;
        }
        return success;
    }

    static async logout(): Promise<boolean> {
        localStorage.removeItem(Authentication.localStorageTokenName);
        storage.clearCredentials()
        return true;
    }

    static async checkConnectedUser(extranetToken: string |null): Promise<boolean|undefined> {
        const config = {withCredentials: true}
        if (extranetToken) {
            config['headers'] = {'Extranet-Authorization' : 'Bearer '+extranetToken}
        }
        return await axios.get<{
            autologout: number,
            login: number,
            access_token?: string,
            refresh_token?: string,
            expires_in?: number,
            token_type?: string
        }>(this.urlSsoCheck, config)
            .then(
                async function success(response) {
                    if (response.status === 200) {
                        if (response.data.access_token) {
                            // New token means user switch from back
                            const tokenInfo = {
                                token_type: response.data.token_type,
                                access_token: response.data.access_token,
                                refresh_token: response.data.refresh_token,
                                expires_in: response.data.expires_in ?? 3600,
                                expires_at: new Date().setSeconds(new Date().getSeconds() + (response.data.expires_in ?? 3600)),
                            }
                            
                            localStorage.setItem(Authentication.localStorageTokenName, JSON.stringify(tokenInfo));

                            // Reload user-information to adjust name & role
                            await Authentication.refreshProfile()

                            return false
                        }
                    }

                    return 1 === response.data.autologout;
                }
            )
            .catch((error)=> {
                return true
            })
        ;
    }

    static async refreshProfile(): Promise<void> {
        await Caller.executeGet(`${envVariable('REACT_APP_API_URL')}/users/me`, {}).then(response => {
            const allowedRoles: string[] = response.user.allowedRoles ?? [response.user.role]
            const allowedAccounts: Array<{id: string, role: string}> = response.user.allowedAccounts ?? []

            localStorage.setItem('profile', JSON.stringify({
                'username': response.user.username,
                'firstname': response.user.firstname,
                'lastname': response.user.lastname,
                'modules': response.user.modules,
                'user_id': response.user.id,
                'allowedRoles': allowedRoles,
                'allowedAccounts': allowedAccounts,
            }))
            localStorage.setItem('role', response.user.role)

            if (!localStorage.getItem('accountId')) {
                const matchingAccount = allowedAccounts.find(account => account.role === response.user.role)
                if (matchingAccount) {
                    localStorage.setItem('accountId', matchingAccount.id)
                }
            }

            // Le compte/rôle sélectionné dans l'extranet (SSO) prime sur le défaut ci-dessus.
            storage.applyPendingSsoContext()
        })
    }
}
